import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeatureName } from '../../types/features';

interface BlockedFeatureModalProps {
  featureName: FeatureName;
  isOpen: boolean;
  onClose: () => void;
}

const featureStaticInfo: Partial<Record<FeatureName, { icon: string; color: string }>> = {
  advancedTasks:     { icon: '✨', color: 'blue' },
  advancedActivities:{ icon: '🚀', color: 'purple' },
  templateLibrary:   { icon: '📚', color: 'emerald' },
  calendar:          { icon: '📅', color: 'indigo' },
  groupGoals:        { icon: '🎯', color: 'orange' },
  advancedRecipes:   { icon: '👨‍🍳', color: 'red' },
  mealPlanCalendar:  { icon: '🗓️', color: 'green' },
  shoppingList:      { icon: '🛒', color: 'cyan' },
  gritiAI:           { icon: '🤖', color: 'purple' },
};

export default function BlockedFeatureModal({ featureName, isOpen, onClose }: BlockedFeatureModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const rawInfo = featureStaticInfo[featureName];
  if (!isOpen || !rawInfo) return null;

  const info = {
    ...rawInfo,
    title: t(`features.${featureName}.title`, { defaultValue: featureName }),
    benefits: [
      t(`features.${featureName}.benefit1`, { defaultValue: '' }),
      t(`features.${featureName}.benefit2`, { defaultValue: '' }),
      t(`features.${featureName}.benefit3`, { defaultValue: '' }),
    ].filter(Boolean),
  };

  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-600 dark:text-purple-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 text-emerald-600 dark:text-emerald-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-600 dark:text-indigo-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-500 text-orange-600 dark:text-orange-400',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-600 dark:text-red-400',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-600 dark:text-green-400',
    cyan: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-500 text-cyan-600 dark:text-cyan-400'
  };

  const bgClass = colorClasses[info.color as keyof typeof colorClasses];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`p-3 rounded-xl border-2 ${bgClass}`}>
              <span className="text-3xl">{info.icon}</span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {info.title}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('common.featureBlocked', { defaultValue: 'Esta funcionalidad está bloqueada' })}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {t('common.unlockBenefits', { defaultValue: 'Desbloquea estas ventajas:' })}
            </p>
            <ul className="space-y-2">
              {info.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.close', { defaultValue: 'Cerrar' })}
            </button>
            <button
              onClick={() => {
                navigate('/settings/personalization');
                onClose();
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors"
            >
              {t('common.goToUnlock', { defaultValue: 'Ir a desbloquear →' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
